/**
 * 极简紫韵模版 - 详情页
 * 
 * 设计理念：全宽渐变Banner标题 + 双栏正文内容 + 右栏Sticky联系人
 * 与所有现有模板（左右分屏/Tab切换/图片上方）完全不同：
 * - 顶部：全宽紫色渐变Banner（不显示图片，纯文字标题+元信息）
 * - 图区：Banner下方横向画廊（非轮播，直接横排展示）
 * - 正文：主内容区左宽 + 联系人面板右窄（Sticky固定）
 * - 底部：相关推荐网格（3列）
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PurpleNavbar from "@/templates/purple/components/Navbar";
import PurpleFooter from "@/templates/purple/components/Footer";
import { getListingDetail, getListings, type ListingDetail as ListingDetailType, type ListingItem } from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function PurpleListingDetail() {
  const { id } = useParams<{ id: string }>();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<ListingDetailType | null>(null);
  const [related, setRelated] = useState<ListingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    window.scrollTo(0, 0);
    getListingDetail(id)
      .then((data) => {
        setListing(data);
        document.title = data?.title ? `${data.title} - 详情` : '详情';
        if (data?.category_id) {
          getListings({ category_id: data.category_id, page_size: 6 })
            .then(d => setRelated((d.list || []).filter(i => i.id !== id).slice(0, 6)))
            .catch(() => {});
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PurpleNavbar />
        <div className="pt-20 animate-pulse">
          <div className="h-48 bg-purple-100" />
          <div className="max-w-5xl mx-auto px-4 py-8 flex gap-8">
            <div className="flex-1 space-y-4">
              <div className="h-6 bg-gray-200 rounded w-3/4" />
              <div className="h-32 bg-gray-200 rounded-2xl" />
            </div>
            <div className="w-72 space-y-4 hidden lg:block">
              <div className="h-48 bg-gray-200 rounded-2xl" />
            </div>
          </div>
        </div>
        <PurpleFooter />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-white">
        <PurpleNavbar />
        <div className="pt-28 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-3xl bg-purple-50">
            <i className="ri-error-warning-line text-3xl text-purple-400" />
          </div>
          <p className="text-gray-500 font-medium text-lg mb-2">信息不存在</p>
          <p className="text-gray-400 text-sm mb-6">该信息可能已被删除或下架</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white text-sm font-bold rounded-2xl hover:from-purple-700 hover:to-violet-700 transition-all shadow-md">
            <i className="ri-arrow-left-line" />返回首页
          </Link>
        </div>
        <PurpleFooter />
      </div>
    );
  }

  const images = listing.images || [];

  return (
    <div className="min-h-screen bg-white">
      <PurpleNavbar />

      {/* ======== 全宽渐变Banner（标题区，无图）======== */}
      <section className="relative pt-20">
        <div className="bg-gradient-to-r from-purple-700 via-violet-700 to-fuchsia-700 py-12 sm:py-16 overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-purple-300/10 blur-3xl" />
          <div className="absolute top-1/4 right-1/3 w-3 h-3 rounded-full bg-white/20" />
          <div className="absolute bottom-1/3 left-1/4 w-4 h-4 rounded-full bg-fuchsia-300/30" />

          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
            {/* 面包屑 */}
            <nav className="flex items-center gap-1.5 text-sm text-white/70 mb-6">
              <Link to="/" className="hover:text-white transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <Link to="/category" className="hover:text-white transition-colors">分类</Link>
              {listing.category?.name && (
                <>
                  <i className="ri-arrow-right-s-line text-xs" />
                  <Link to={`/category/${listing.category_id}`} className="hover:text-white transition-colors">
                    {listing.category.name}
                  </Link>
                </>
              )}
            </nav>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight mb-4">
              {listing.title}
            </h1>

            <div className="flex items-center gap-4 flex-wrap">
              {listing.category?.name && (
                <span className="text-sm px-3 py-1 bg-white/15 backdrop-blur-sm text-white rounded-full font-medium">
                  {listing.category.name}
                </span>
              )}
              {listing.is_pinned && (
                <span className="text-sm px-3 py-1 bg-amber-400/30 backdrop-blur-sm text-white rounded-full font-bold flex items-center gap-1">
                  <i className="ri-pushpin-line text-xs" />置顶
                </span>
              )}
              <span className="text-sm text-white/70 flex items-center gap-1">
                <i className="ri-time-line" />{formatDate(listing.created_at)}
              </span>
              <span className="text-sm text-white/70 flex items-center gap-1">
                <i className="ri-eye-line" />{listing.view_count || 0} 次浏览
              </span>
              {listing.location && (
                <span className="text-sm text-white/70 flex items-center gap-1">
                  <i className="ri-map-pin-line" />{listing.location}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ======== 图片画廊（Banner下方，主图+缩略图无缝拼接）======== */}
      {images.length > 0 && (
        <section className="relative -mt-8 z-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              {/* 主图 */}
              <div className="w-full aspect-[16/9] sm:aspect-[2/1] bg-purple-50 overflow-hidden cursor-pointer"
                   onClick={() => { setCurrentImage(0); setLightboxOpen(true); }}>
                <img
                  src={images[currentImage]}
                  alt={listing.title}
                  className="w-full h-full object-contain"
                />
              </div>
              {/* 缩略图条 - 无缝拼接在主图下方 */}
              {images.length > 1 && (
                <div className="flex border-t border-gray-100">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImage(i)}
                      className={`shrink-0 w-20 h-20 sm:w-24 sm:h-24 overflow-hidden border-r border-gray-100 last:border-r-0 transition-all cursor-pointer ${
                        currentImage === i
                          ? 'ring-2 ring-purple-500 ring-inset'
                          : 'hover:opacity-80'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ======== 正文：双栏布局 ======== */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* 左栏：内容 */}
          <div className="flex-1 min-w-0">
            {/* 操作按钮 */}
            <div className="flex items-center gap-2 mb-6">
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-500 hover:text-purple-600 hover:border-purple-200 hover:bg-purple-50 transition-all text-sm">
                <i className="ri-share-forward-line" />分享
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-500 hover:text-pink-500 hover:border-pink-200 hover:bg-pink-50 transition-all text-sm">
                <i className="ri-heart-line" />收藏
              </button>
            </div>

            {/* 详细描述 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm">
              <h3 className="text-base font-bold text-gray-800 mb-5 flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center">
                  <i className="ri-file-text-line text-purple-500 text-xs" />
                </div>
                详细描述
              </h3>
              {listing.content ? (
                <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap break-words [&_a]:text-purple-600 [&_strong]:text-gray-800 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1">
                  {listing.content}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">暂无详细描述</p>
              )}

              {/* 标签 */}
              {listing.tags && listing.tags.length > 0 && (
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {listing.tags.map((tag: string, i: number) => (
                      <span key={i} className="text-xs px-3 py-1.5 bg-purple-50 text-purple-600 rounded-full font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 右栏：联系面板（Sticky） */}
          <div className="lg:w-80 shrink-0">
            <div className="lg:sticky lg:top-24 space-y-4">
              {/* 发布者信息卡片 */}
              <div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 via-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-md shadow-purple-200">
                    {listing.contact_name ? listing.contact_name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{listing.contact_name || "匿名用户"}</div>
                    <div className="text-xs text-gray-400">{formatDate(listing.created_at)} 发布</div>
                  </div>
                </div>

                {/* 公司名称 */}
                {siteConfig && listing.company_name && !siteConfig.hide_company_name && (
                  <div className="mb-4 p-3 bg-purple-50/50 rounded-xl">
                    <span className="text-[11px] text-gray-400 uppercase">公司名称</span>
                    <p className="text-sm font-medium text-gray-700 mt-0.5">{listing.company_name}</p>
                  </div>
                )}

                {/* 联系方式 */}
                <div className="space-y-2.5">
                  {listing.contact_phone && (
                    <a
                      href={`tel:${listing.contact_phone}`}
                      className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl hover:from-purple-100 hover:to-violet-100 transition-colors group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-purple-500 flex items-center justify-center shrink-0 shadow-sm">
                        <i className="ri-phone-line text-white text-sm" />
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-400">联系电话</div>
                        <div className="text-sm font-semibold text-gray-800 group-hover:text-purple-600 transition-colors">
                          {listing.contact_phone}
                        </div>
                      </div>
                    </a>
                  )}
                  {listing.contact_wechat && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                      <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center shrink-0 shadow-sm">
                        <i className="ri-wechat-line text-white text-sm" />
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-400">微信</div>
                        <div className="text-sm font-semibold text-gray-800">{listing.contact_wechat}</div>
                      </div>
                    </div>
                  )}
                  {listing.contact_email && (
                    <a
                      href={`mailto:${listing.contact_email}`}
                      className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl hover:from-blue-100 hover:to-cyan-100 transition-colors group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center shrink-0 shadow-sm">
                        <i className="ri-mail-line text-white text-sm" />
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-400">电子邮箱</div>
                        <div className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                          {listing.contact_email}
                        </div>
                      </div>
                    </a>
                  )}
                  {!listing.contact_phone && !listing.contact_wechat && !listing.contact_email && (
                    <p className="text-gray-400 text-sm text-center py-4 bg-gray-50 rounded-xl">暂未提供联系方式</p>
                  )}
                </div>
              </div>

              {/* 快速操作 */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
                <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">快速操作</h4>
                <Link
                  to="/publish"
                  className="flex items-center justify-center gap-2 w-full py-3 border-2 border-purple-200 text-purple-600 font-semibold rounded-xl hover:bg-purple-50 transition-all text-sm"
                >
                  <i className="ri-add-line" />发布信息
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ======== 相关推荐 3列网格 ======== */}
        {related.length > 0 && (
          <section className="mt-14">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-1 h-5 bg-purple-500 rounded-full" />
              <h2 className="text-lg font-bold text-gray-800">相关推荐</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-purple-200 transition-all flex flex-col"
                >
                  <div className="aspect-[4/3] bg-purple-50 overflow-hidden">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-purple-300 text-2xl" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-purple-600 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2.5 text-[11px] text-gray-400">
                      <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium text-[10px]">
                        {item.category?.name || "未分类"}
                      </span>
                      <span>{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ======== 图片灯箱 ======== */}
      {lightboxOpen && images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setCurrentImage((currentImage - 1 + images.length) % images.length); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}
          <img
            src={images[currentImage]}
            alt=""
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setCurrentImage((currentImage + 1) % images.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <i className="ri-arrow-right-s-line text-2xl" />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setCurrentImage(i); }}
                    className={`w-2 h-2 rounded-full transition-all ${i === currentImage ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ======== 移动端底部浮动联系按钮 ======== */}
      {listing.contact_phone && (
        <div className="fixed bottom-0 inset-x-0 md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 px-4 py-3 z-40">
          <div className="flex items-center gap-3">
            <a
              href={`tel:${listing.contact_phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white font-bold rounded-2xl shadow-lg shadow-purple-500/30"
            >
              <i className="ri-phone-line text-lg" />拨打电话
            </a>
            {listing.contact_wechat && (
              <button className="shrink-0 w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-600">
                <i className="ri-wechat-line text-lg" />
              </button>
            )}
          </div>
        </div>
      )}

      <PurpleFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
